import { useCallback, useState, useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router'
import { requireAuth } from '../wrappers/requireAuth'
import { useUser } from "../hooks/useUser"
import { useAsyncAction } from '../hooks/useAsyncAction'
import { useAsyncEffect } from '../hooks/useAsyncEffect'
import { useClient } from '../providers/supabase'
import { useBackendApi } from '../hooks/useBackendApi'
import { updateMySettings } from '@healthshare/shared/commands/account'
import { updateMyMembership } from '@healthshare/shared/commands/memberships'
import { useAppDispatch, useAppSelector } from '../hooks/reduxToolkit'
import { setAliceHealthDataConsent, setSettings } from '../reduxToolkit/membershipSlice'
import { merge } from "lodash";
import { useLoadSettingsFromServer } from '../hooks/useLoadSettingsFromServer'
import { isRateLimitedError } from '../components/error-boundary'
import { MembershipUpdate, SettingsRow, SettingsUpdate } from '../../types/supabase'
import { TextractOptInCheckbox } from '../components/forms/textractOptInCheckbox'
import { Tooltip } from '../components/tooltip'
import { usePushNotifications } from '../providers/pushNotifications'
import { useSentry } from '../providers/sentry'
import { TwoFactorSettings } from '../components/forms/twoFactorSettings'
import { safeAdminReturnTo } from '../screens/adminMfaRequired'
import { ChangePasswordForm } from '../components/forms/changePasswordForm'

function EditSettings() {
  const user = useUser()
  if (!user) { throw new Error(`Not signed in!`) }

  const [searchParams] = useSearchParams()
  const location = useLocation()
  const adminReturnTo = searchParams.get('returnTo')
  const api = useBackendApi()
  const settings = useAppSelector((s) => s.membership?.settings)
  const dispatch = useAppDispatch()
  const [passwordChanged, setPasswordChanged] = useState(false)

  const { error: loadError } = useLoadSettingsFromServer()

  useEffect(() => {
    if (!passwordChanged) return
    const id = setTimeout(() => setPasswordChanged(false), 5000)
    return () => clearTimeout(id)
  }, [passwordChanged])

  useEffect(() => {
    if (location.hash !== '#two-factor') return
    document.getElementById('two-factor')?.scrollIntoView?.()
  }, [location.hash])

  const [{error, loading}, updateSettings] = useAsyncAction(async (update: SettingsUpdate) => {
    // The endpoint stamps `updated_at` and returns the saved row, so the slice
    // gets the server's values rather than a locally merged guess.
    const saved = await updateMySettings(api, update)

    dispatch(setSettings({
      settings: merge({}, settings, saved)
    }))
  }, [api, dispatch, settings])

  return <div className='row'>
    <div className='col-12'>
      <h1 className='text-center'>Edit your Settings</h1>

      {loadError && <div className='alert alert-danger' role='alert'>
        {isRateLimitedError(loadError)
          ? 'Too many requests were sent in a short time. Wait a minute, then try again.'
          : loadError.message}
      </div>}

      <div className='mt-4'>
        <SettingsForm settings={settings} loading={loading} onUpdate={updateSettings} />
      </div>
      {error && <div className='alert alert-danger' role='alert'>
        {error.message}
      </div>}

      <hr />
      <h4>Change Password</h4>
      <div className='mt-2'>
        <ChangePasswordForm onChangePasswordSuccess={() => setPasswordChanged(true)} />
        {passwordChanged && (
          <div className='alert alert-success' role='alert'>
            Password changed successfully.
          </div>
        )}
      </div>

      <hr />
      <div id="two-factor" className='mt-2'>
        <h4>Two-Factor Authentication</h4>
        <TwoFactorSettings
          onEnabled={adminReturnTo
            ? () => { window.location.assign(safeAdminReturnTo(adminReturnTo)) }
            : undefined}
        />
      </div>
    </div>
  </div>
}

export default requireAuth(EditSettings)

interface SettingsFormProps {
  settings: SettingsRow | null,
  loading?: boolean,
  onUpdate: (settings: SettingsUpdate) => void
}

function SettingsForm({settings, loading, onUpdate}: SettingsFormProps) {
  const [unsavedSettings, setUnsavedSettings] = useState<SettingsUpdate>(settings || {})
  
  const update = useCallback((update: SettingsUpdate) => {
    setUnsavedSettings((s) => merge({}, s, update))
    onUpdate(update)
  }, [onUpdate])

  return <form className="settings-form">
    <h4>Notification Settings</h4>
    <p>
      I want to receive notifications by:
    </p>
    <div className='form-group'>
      <AllowPushNotificationsCheckbox
        value={unsavedSettings.notification_prefs_push || false}
        disabled={loading && (unsavedSettings.notification_prefs_push !== settings?.notification_prefs_push)}
        onChange={(value) => {
          update({notification_prefs_push: value})
        }}
        />
    </div>
    <div className='form-group'>
      <div className='form-check form-switch'>
        <input type='checkbox' className='form-check-input'
          id='email-notifications-checkbox'
          checked={unsavedSettings.notification_prefs_email || false}
          onChange={(e) => update({notification_prefs_email: e.target.checked})} />
        <label className='form-check-label' htmlFor='email-notifications-checkbox'>Email</label>
      </div>
      {/* Grouped with the Email toggle above: this consent only matters once email
          is on, and writes to the membership rather than the settings row, so it
          manages itself. */}
      <AliceHealthDataConsentSetting />
    </div>

    <hr />
    <h4>Opt-In Settings</h4>
    <div className='form-group'>
      <TextractOptInCheckbox
        value={unsavedSettings.textract_opt_in || null}
        disabled={loading && (unsavedSettings.textract_opt_in !== settings?.textract_opt_in)}
        onChange={(value) => {
          update({textract_opt_in: value})
        }} />
    </div>

    <hr />
    <h4>Privacy Settings</h4>
    <div className='form-group'>
      <div className='form-check form-switch'>
        <input type='checkbox' className='form-check-input'
          id='conversation-data-opt-out-checkbox'
          checked={unsavedSettings.conversation_data_opt_out || false}
          onChange={(e) => update({conversation_data_opt_out: e.target.checked})} />
        <label className='form-check-label' htmlFor='conversation-data-opt-out-checkbox'>
          Opt out of conversation data logging
          <small className="text-muted d-block">
            When enabled, your conversation details will not be recorded. Only token usage, model, and use case will be logged.
          </small>
        </label>
      </div>
    </div>
  </form>
}

/**
 * Affirmative consent for Alice to discuss the member's health information over
 * email. It covers email replies and nothing else: push goes through Expo/FCM,
 * who are not BAA partners, so every push is a content-free doorbell whatever
 * this says (`Notification::pushTitle()`).
 *
 * Unlike everything else on this screen, this is stored on the membership rather
 * than the settings row — it is a property of the membership whose data Alice would
 * be handling, and the same flag can be set by telling Alice directly in a
 * conversation. Every change is logged to `membership_consent_events` by a database
 * trigger, so the toggle needs no separate bookkeeping.
 */
function AliceHealthDataConsentSetting() {
  const client = useClient()
  const api = useBackendApi()
  const dispatch = useAppDispatch()
  const membershipId = useAppSelector((s) => s.membership?.membershipId)
  const consentedAt = useAppSelector((s) => s.membership?.aliceHealthDataConsentAt)

  // Consent can also be given (or withdrawn) by talking to Alice, so the persisted
  // slice value may predate that. Re-read it when the screen opens rather than
  // showing the member a toggle that disagrees with what Alice was told.
  useAsyncEffect(async () => {
    if (!membershipId) { return }

    const result = await client.from('memberships')
      .select('alice_health_data_consent_at')
      .eq('id', membershipId)
      .maybeSingle()

    if (result.error) { throw result.error }

    dispatch(setAliceHealthDataConsent({
      consentedAt: result.data?.alice_health_data_consent_at ?? null,
    }))
  }, [client, dispatch, membershipId])

  const [{error, loading}, updateConsent] = useAsyncAction(async (granted: boolean) => {
    if (!membershipId) { throw new Error('No membership to record consent against') }

    const now = new Date().toISOString()
    const update: MembershipUpdate = granted
      ? {
        alice_health_data_consent_at: now,
        alice_health_data_consent_source: 'web-settings',
        // Only consent captured in a conversation cites a message; the database
        // rejects a message id paired with any other source.
        alice_health_data_consent_message_id: null,
      }
      : {
        alice_health_data_consent_at: null,
        alice_health_data_consent_source: 'web-settings',
        alice_health_data_consent_message_id: null,
        alice_health_data_consent_revoked_at: now,
      }

    await updateMyMembership(api, membershipId, update)

    dispatch(setAliceHealthDataConsent({ consentedAt: granted ? now : null }))
  }, [api, dispatch, membershipId])

  return <div className='form-group mt-3'>
    <div className='form-check form-switch'>
      <input type='checkbox' className='form-check-input'
        id='alice-health-data-consent-checkbox'
        checked={!!consentedAt}
        disabled={loading || !membershipId}
        onChange={(e) => updateConsent(e.target.checked)} />
      <label className='form-check-label' htmlFor='alice-health-data-consent-checkbox'>
        Let Alice discuss my health information by email
        <small className="text-muted d-block">
          To be useful over email, Alice has to name the things she's writing about:
          your medical expenses, the providers you saw, your bills and the conditions
          they were for. Email isn't a secure channel — messages can be read by anyone
          who has access to your inbox, can sometimes sit unencrypted on mail servers along the
          way, and can go to the wrong person if the address is mistyped. If you'd
          rather she didn't, turn email off above; Without it she can still help you here in
          the app. You can turn this off again at any time.
        </small>
      </label>
    </div>
    {error && <div className='alert alert-danger mt-2' role='alert'>
      {error.message}
    </div>}
  </div>
}

interface AllowPushNotificationsCheckboxProps {
  value: boolean
  disabled?: boolean
  onChange: (value: boolean) => void
}

function AllowPushNotificationsCheckbox({ value, disabled, onChange }: AllowPushNotificationsCheckboxProps) {
  const pushNotifications = usePushNotifications()
  const Sentry = useSentry()
  const [error, setError] = useState<Error | null>(null)
  
  const permissionState = pushNotifications.getPermissionState()
  
  console.log('permissionState', permissionState, pushNotifications.constructor.name)

  const handleChange = async (checked: boolean) => {
    if (checked) {
      try {
        await pushNotifications.register()
        onChange(true)
      } catch (error) {
        console.error('Error requesting notification permission:', error)
        Sentry.captureException(error)
        setError(error as Error)
      }
    } else {
      onChange(false)
    }
  }

  return (
    <div className='form-check form-switch'>
      <Tooltip tooltip={permissionState == 'not-supported' ? 'Push notifications are not supported by your phone or browser' : undefined}>
        <input
          type='checkbox'
          className={`form-check-input ${disabled || permissionState == 'not-supported' ? 'disabled' : ''}`}
          id='notifications-checkbox'
          checked={value}
          onChange={(e) => {
            if (disabled || permissionState == 'not-supported') {
              return
            }
            handleChange(e.target.checked)
          }}
          />
      </Tooltip>
      <label className='form-check-label' htmlFor='notifications-checkbox'>
        Phone Push Notifications
        <small className="text-muted d-block">
            A notification on your phone and a message in the app.
        </small>
        {permissionState === 'denied' && (
          <small className="text-danger d-block">
            Notifications are blocked. Please enable them in your phone or browser settings.
          </small>
        )}
        {error && (
          <small className="text-danger d-block">
            {error.message}
          </small>
        )}
      </label>
    </div>
  )
}

